/**
 * Session Engine v3 — EXPLORATORY / non-Canon prototype.
 *
 * The five step panels (S1–S5). Each step is either declared or determined
 * collaboratively; collaborative steps show the 3-question tool framing above
 * the step's editor. The editors themselves are the "record the result"
 * surface used in both modes.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { resolveLoopIdForSession } from '../../../lib/catchballSessionLink';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { Plus, Trash2, Users, ListChecks, SlidersHorizontal, Hash, Trophy, AlertCircle, ArrowUp, ArrowDown, ArrowRight, Play, ExternalLink, Download, Library, CheckCircle2, Lock, Unlock, ChevronDown, ScrollText, Save } from 'lucide-react';
import { ItemInputModal, type PlatformItem, type CollectionWithItems } from './ItemInputModal';
import { SaveItemPoolModal } from './SaveItemPoolModal';
import { planItemPoolSave } from '../saveItemPool';
import { DriverAlignmentPanel } from './DriverAlignmentPanel';
import { ComparisonSetValidationPanel } from './ComparisonSetValidationPanel';
import { LiftToProposalModal } from '../../../components/prioritization/governance/LiftToProposalModal';
import { useComparabilityBlocks } from '../useComparabilityBlocks';
import { ToolFraming } from './ToolFraming';
import { ScenarioBoard } from './ScenarioBoard';
import {
  canRunRanking,
  getNextRightThing,
  isStepMandatory,
  isStepSatisfied,
  scoreItem,
  stepModeAvailability,
  unmetRequirements,
} from '../engineLogic';
import { useDerivedExclusivity } from '../useExclusivity';
import { useSessionEngineStore } from '../store';
import { STEP_META, type ParticipantRole, type StepId } from '../types';
import { useEffectiveTrackParticipants } from '../../../hooks/useEffectiveTrackParticipants';
import { useAttributesStore } from '../../../stores/prioritization/attributesStore';
import { useCycleParticipantsStore } from '../../../stores/prioritization/cycleParticipantsStore';
import { useTrackParticipantsStore } from '../../../stores/prioritization/trackParticipantsStore';
import { useSystemUsers } from '../../../hooks/useSystemUsers';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useCollectionsStore } from '../../../stores/prioritization/collectionsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useSessionStore } from '../../../stores/prioritization/sessionStore';
import { useTrackStepLocks } from '../../../hooks/useTrackStepLocks';
import { TrackStepNote } from './TrackStepNote';
import { ScoringScaleChoice } from '../../../components/prioritization/session/ScoringScaleChoice';
import {
  isSameScoringScale,
  normalizeToolConfig,
  readToolSettings,
  scoringScaleLabel,
  scoringScaleToolConfig,
  type ScoringScaleRange,
} from '../../../lib/toolSettings';
import { useExchangeContext } from '../../../hooks/prioritization/useExchangeContext';
import { buildCatchballInboxUrl } from '../../../lib/catchballInboxUrls';
import { humanizeAttributeKey, normalizeCriteriaWeights } from '../../../lib/sessionCriteriaFromTrack';
import { describeTrackWeights } from '../../../lib/trackCriteriaWeights';
import type { StepFacet } from '../stepFacets';
import { ItemRelationshipFlags } from '@/components/prioritization/ItemRelationshipFlags';
import { itemInContextHref } from '@/lib/itemCycleStanding';
import { keeperNote, needsTheRoom, s4CellPolicy, valuesFromSentence } from '@/lib/valuesFrom';
import { useAttributeKeepers } from '@/hooks/prioritization/useAttributeKeepers';

interface StepPanelProps {
  step: StepId;
  /** Frame = declare content only; goal = Main Event (tool + run). */
  facet?: StepFacet;
  platformItems: PlatformItem[];
  onSelectStep: (step: StepId) => void;
}

const STEP_ICON: Record<StepId, React.ComponentType<{ size?: number; className?: string }>> = {
  S1: Users,
  S2: ListChecks,
  S3: SlidersHorizontal,
  S4: Hash,
  S5: Trophy,
};

export function StepPanel({ step, facet = 'frame', platformItems, onSelectStep }: StepPanelProps) {
  const event = useSessionEngineStore((s) => s.event);
  const setStepMode = useSessionEngineStore((s) => s.setStepMode);
  const unlockStep = useSessionEngineStore((s) => s.unlockStep);
  const { steps: trackSteps, trackWeights } = useTrackStepLocks(event?.trackId);
  const trackStep = step === 'S5' ? null : trackSteps?.[step];
  const stepLocked = trackStep?.disposition === 'locked';
  const isFrameOnly =
    facet === 'frame' && step === 'S2' && event?.primaryGoalStep === 'S2';

  const rawCollections = useCollectionsStore((s) => s.collections);
  const getItemIdsInCollection = useCollectionsStore((s) => s.getItemIdsInCollection);
  const storeItems = useItemsStore((s) => s.items);
  const itemById = useMemo(() => new Map(storeItems.map((i) => [i.id, i])), [storeItems]);
  const collections: CollectionWithItems[] = useMemo(
    () =>
      rawCollections.map((c) => ({
        id: c.id,
        name: c.name,
        description: c.description,
        itemIds: getItemIdsInCollection(c.id).filter((id) => itemById.has(id)),
      })).filter((c) => c.itemIds.length > 0),
    [rawCollections, getItemIdsInCollection, itemById],
  );
  const [unlockReason, setUnlockReason] = useState('');
  const [showUnlockPrompt, setShowUnlockPrompt] = useState(false);

  const satisfied = event ? isStepSatisfied(event, step) : false;
  const goalStep = event?.primaryGoalStep ?? 'S5';
  const nextRightThing = event ? getNextRightThing(event) : 'S1' as const;
  // Advances to the next Frame step, or to the Main Event goal (e.g. S2),
  // never to an out-of-scope S5 when this event's goal is item-pool selection.
  const doneTarget = satisfied && nextRightThing !== step ? nextRightThing : null;

  if (!event) return null;

  const cfg = event.steps[step];
  const Icon = STEP_ICON[step];
  // What this step can be answered as, right now, and why not when it can't.
  const modeAvailability = {
    declared: stepModeAvailability(event, step, 'declared'),
    collaborative: stepModeAvailability(event, step, 'collaborative'),
  } as const;
  const mandatory = isStepMandatory(event, step);

  return (
    <div className="space-y-5">
      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 flex h-9 w-9 items-center justify-center bg-slate-blue-100 dark:bg-slate-blue-900/40 text-slate-blue-800 dark:text-slate-blue-300">
            <Icon size={18} />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {step} · {STEP_META[step].label}
              {mandatory ? (
                <span className="ml-2 align-middle text-[10px] font-semibold uppercase text-luminous-coral-700 dark:text-luminous-coral-400">required</span>
              ) : (
                <span className="ml-2 align-middle text-[10px] font-semibold uppercase text-[var(--text-ter)] dark:text-[var(--text-ter)]">optional</span>
              )}
            </h2>
            <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">{STEP_META[step].blurb}</p>
          </div>
        </div>
        <span
          className={[
            'rounded-full px-2.5 py-0.5 text-xs font-medium',
            satisfied ? 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300' : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-ter)]',
          ].join(' ')}
        >
          {satisfied ? 'Satisfied' : 'Pending'}
        </span>
      </header>

      {/* Locked banner — shown when a collaborative step has been run and completed */}
      {cfg.runStatus === 'complete' && (
        <div className="border border-electric-mustard-200 dark:border-electric-mustard-700/40 bg-electric-mustard-50 dark:bg-electric-mustard-900/10 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Lock size={14} className="shrink-0 text-electric-mustard-700 dark:text-electric-mustard-400" />
              <span className="text-sm font-medium text-electric-mustard-800 dark:text-electric-mustard-300">
                This step was run collaboratively and is locked.
              </span>
            </div>
            <button
              onClick={() => setShowUnlockPrompt((v) => !v)}
              className="flex items-center gap-1.5 rounded border border-electric-mustard-200 dark:border-electric-mustard-700/50 px-2.5 py-1 text-xs font-medium text-electric-mustard-800 dark:text-electric-mustard-300 hover:bg-electric-mustard-100 dark:hover:bg-electric-mustard-900/30 transition-colors"
            >
              <Unlock size={11} /> Unlock to revise
            </button>
          </div>
          {showUnlockPrompt && (
            <div className="mt-3 space-y-2">
              <input
                type="text"
                value={unlockReason}
                onChange={(e) => setUnlockReason(e.target.value)}
                placeholder="Optional: why are you revising this step? (saved to history)"
                className="w-full rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder-gray-400 dark:placeholder-[var(--text-ter)] focus:border-electric-mustard-500 focus:outline-none"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    unlockStep(step, unlockReason || undefined);
                    setShowUnlockPrompt(false);
                    setUnlockReason('');
                  }}
                  className="rounded bg-electric-mustard-700 px-3 py-1 text-xs font-semibold text-black hover:bg-electric-mustard-600 transition-colors"
                >
                  Confirm unlock
                </button>
                <button
                  onClick={() => { setShowUnlockPrompt(false); setUnlockReason(''); }}
                  className="rounded border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {/* Step history for this step */}
          {(event.stepHistory ?? []).filter((h) => h.step === step).length > 0 && (
            <div className="mt-3 border-t border-electric-mustard-200 dark:border-electric-mustard-800/30 pt-2 space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-ter)] dark:text-[var(--text-ter)]">History</p>
              {(event.stepHistory ?? []).filter((h) => h.step === step).map((h, i) => (
                <div key={i} className="flex items-baseline gap-2 text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  <span className="font-mono text-[var(--text-ter)] dark:text-[var(--text-ter)]">{new Date(h.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                  <span className="capitalize text-[var(--text-sec)] dark:text-[var(--text-sec)]">{h.action}</span>
                  {h.note && <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">— {h.note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {isFrameOnly ? (
        <div className="border border-slate-blue-200 dark:border-slate-blue-800 bg-slate-blue-50 dark:bg-slate-blue-900/20 px-4 py-3 text-sm text-slate-blue-900 dark:text-slate-blue-100">
          <p className="font-medium">Frame — item pool</p>
          <p className="mt-1 text-xs text-slate-blue-800/90 dark:text-slate-blue-200/90">
            Declare or select the manageable set so criteria and values can proceed.
            Do not start a collaborative sitting here — when Frame conditions are met,
            open <strong>Main Event → Items</strong> to choose a tool and run S2.
          </p>
        </div>
      ) : (
        <>
          <div className="inline-flex border border-[var(--rule)] dark:border-[var(--rule-bright)] p-0.5 text-sm">
            {(['declared', 'collaborative'] as const).map((m) => {
              const modeOk = m === cfg.mode || modeAvailability[m].available;
              return (
                <button
                  key={m}
                  type="button"
                  onClick={() => modeOk && setStepMode(step, m)}
                  disabled={!modeOk}
                  title={modeOk ? undefined : modeAvailability[m].reason}
                  className={[
                    ' px-3 py-1.5 font-medium capitalize transition',
                    cfg.mode === m
                      ? 'bg-slate-blue-600 text-white'
                      : modeOk
                        ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]'
                        : 'cursor-not-allowed text-[var(--text-ter)] dark:text-[var(--text-ter)] opacity-40',
                  ].join(' ')}
                >
                  {m}
                </button>
              );
            })}
          </div>
          <p className="-mt-3 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {cfg.mode === 'declared'
              ? 'Declared — one person or a recorded team decision states the answer.'
              : 'Collaborative — the group determines the answer using a tool.'}
          </p>
          {/* Say why a mode is off — never hide it silently. */}
          {!modeAvailability.collaborative.available && cfg.mode !== 'collaborative' && (
            <p className="-mt-3 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
              {modeAvailability.collaborative.reason}
            </p>
          )}
          {!modeAvailability.declared.available && cfg.mode === 'declared' && (
            <p className="-mt-3 text-xs text-[var(--mustard)] dark:text-[var(--mustard)]">
              {modeAvailability.declared.reason}
            </p>
          )}
        </>
      )}

      <TrackStepNote step={trackStep} />

      {/* Step content first — fill this before deciding how to run it. A step locked
          on the track stays in view and takes no input (owner ruling 2026-07-27). */}
      <div
        inert={stepLocked && step !== 'S1'}
        className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-4"
      >
        {step === 'S1' && <ParticipantsEditor locked={stepLocked} />}
        {step === 'S2' && <ItemsEditor platformItems={platformItems} collections={collections} />}
        {step === 'S3' && <CriteriaEditor trackLocked={stepLocked} trackWeights={trackWeights ?? null} />}
        {step === 'S4' && <ValuesEditor />}
        {step === 'S5' && <RankingEditor />}
      </div>

      {/* Run collaboratively — Main Event only; never on Frame item pool */}
      {!isFrameOnly && cfg.mode === 'collaborative' && (
        <ToolFraming step={step} itemCount={event.items.length} />
      )}

      {/* "Done" — advance within Frame, or to Main Event (primary goal), never
          to S5 when this event's goal is S2 item-pool selection. */}
      {satisfied &&
        doneTarget &&
        doneTarget !== step &&
        !(facet === 'goal' && cfg.runStatus === 'complete') && (
        <div className="flex items-center justify-between gap-3 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            <CheckCircle2 size={14} className="shrink-0 text-alpine-green-600 dark:text-alpine-green-400" />
            <span>
              {doneTarget === goalStep && step !== goalStep
                ? 'Frame conditions met — ready for the Main Event'
                : `${step} satisfied — ready to continue when you are`}
            </span>
          </div>
          <button
            type="button"
            onClick={() => onSelectStep(doneTarget)}
            className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-alpine-green-600 hover:bg-alpine-green-50 dark:hover:bg-alpine-green-900/20 hover:text-alpine-green-800 dark:hover:text-alpine-green-300 transition-colors"
          >
            Done — go to {doneTarget === goalStep ? `Main Event (${doneTarget})` : doneTarget}
            <ArrowRight size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── S1 ──────────────────────────────────────────────────────────────────────

/** Locked on the track, the roster takes no edits; bringing the track's people in stays open. */
function ParticipantsEditor({ locked }: { locked: boolean }) {
  const event = useSessionEngineStore((s) => s.event)!;
  const addParticipant = useSessionEngineStore((s) => s.addParticipant);
  const removeParticipant = useSessionEngineStore((s) => s.removeParticipant);
  const setParticipantRole = useSessionEngineStore((s) => s.setParticipantRole);
  const [search, setSearch] = useState('');

  const isFrameLinked = event.contextMode === 'cycle_linked' && !!event.trackId;
  const currentWorkspaceId = useTenantStore((s) => s.getCurrentWorkspace)()?.id;
  const { users: wsUsers } = useSystemUsers({ workspaceId: currentWorkspaceId });

  const fetchCycleParticipants = useCycleParticipantsStore((s) => s.fetchParticipantsForCycle);
  const fetchTrackParticipants = useTrackParticipantsStore((s) => s.fetchParticipantsForTrack);
  const { effective: frameParticipants, loading: frameLoading } = useEffectiveTrackParticipants(
    isFrameLinked ? event.cycleId : undefined,
    isFrameLinked ? event.trackId : undefined,
  );

  useEffect(() => {
    if (!isFrameLinked) return;
    if (event.cycleId) void fetchCycleParticipants(event.cycleId);
    if (event.trackId) void fetchTrackParticipants(event.trackId);
  }, [isFrameLinked, event.cycleId, event.trackId, fetchCycleParticipants, fetchTrackParticipants]);

  const addedUserIds = useMemo(() => new Set(event.participants.map((p) => p.userId).filter(Boolean)), [event.participants]);
  const frameParticipantIds = useMemo(() => new Set(frameParticipants.map((p) => p.userId)), [frameParticipants]);

  const availableUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return wsUsers.filter((u) => {
      if (addedUserIds.has(u.id)) return false;
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
    });
  }, [wsUsers, addedUserIds, search]);

  function add(userId: string, displayName: string) {
    addParticipant(displayName, 'contributor', userId);
  }

  function importFromFrame() {
    for (const fp of frameParticipants) {
      if (addedUserIds.has(fp.userId)) continue;
      const user = wsUsers.find((u) => u.id === fp.userId);
      const displayName = user?.name || user?.email || fp.userId;
      addParticipant(displayName, 'contributor', fp.userId);
    }
  }

  function getInitials(name: string) {
    return name.split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase() || '?';
  }

  return (
    <div className="space-y-4">
      {/* Track inheritance banner */}
      {isFrameLinked && (
        <div className={`flex items-center justify-between gap-3  px-3 py-2.5 ${event.participants.length > 0 ? 'bg-alpine-green-50 border border-alpine-green-200 dark:bg-alpine-green-900/20 dark:border-alpine-green-800/50' : 'bg-slate-blue-50 border border-slate-blue-200 dark:bg-slate-blue-600/10 dark:border-slate-blue-500/30'}`}>
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            {frameLoading
              ? 'Loading track participants…'
              : event.participants.length > 0
                ? <><span className="text-alpine-green-800 dark:text-alpine-green-300 font-medium">{event.participants.length} participant{event.participants.length === 1 ? '' : 's'} inherited</span> from <strong>{event.trackName ?? 'this track'}</strong></>
                : `${frameParticipants.length} participant${frameParticipants.length === 1 ? '' : 's'} available in ${event.trackName ?? 'this track'}`}
          </p>
          {event.participants.length === 0 && (
            <button
              onClick={importFromFrame}
              disabled={frameLoading || frameParticipants.length === 0}
              className="flex shrink-0 items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-1.5 text-xs font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
            >
              <Download size={13} /> Import from Track
            </button>
          )}
        </div>
      )}

      <div inert={locked} className="space-y-4">
        {/* Selected participants */}
        {event.participants.length > 0 && (
          <div className="space-y-0.5">
            {event.participants.map((p) => {
              const isUUID = /^[0-9a-f-]{36}$/i.test(p.name);
              const wsUser = isUUID && p.userId ? wsUsers.find((u) => u.id === p.userId) : null;
              const displayName = wsUser ? (wsUser.name || wsUser.email) : p.name;
              const sourceLabel = isFrameLinked && p.userId && frameParticipantIds.has(p.userId) ? 'Track' : 'Authored';
              return (
                <div key={p.id} className="flex items-center gap-2.5 px-2 py-1.5 bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)] group transition-colors">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-blue-600/25 text-[10px] font-bold text-slate-blue-800 dark:text-slate-blue-300">
                    {getInitials(displayName)}
                  </span>
                  <span className="flex-1 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{displayName}</span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${sourceLabel === 'Track' ? 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300' : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-ter)]'}`}>
                    {sourceLabel}
                  </span>
                  {/* F7: this used to be an unlabeled <select> styled like a chip,
                      so assigning the decider read as an action rather than a
                      choice. Labelled, given the design system's field styling
                      and a chevron, and it now changes the role in place instead
                      of remove-then-add (which raced and could drop the person). */}
                  <label className="flex shrink-0 items-center gap-1.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      Role
                    </span>
                    <span className="relative">
                      <select
                        value={p.role}
                        aria-label={`Role for ${displayName}`}
                        onChange={(e) => setParticipantRole(p.id, e.target.value as ParticipantRole)}
                        className="appearance-none border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface-raised)] py-1 pl-2 pr-6 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)] outline-none transition-all focus:border-slate-blue-300 focus:ring-2 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-900/40"
                      >
                        <option value="contributor">Contributor</option>
                        <option value="decider">Decider</option>
                        <option value="observer">Observer</option>
                        <option value="facilitator">Facilitator</option>
                      </select>
                      <ChevronDown
                        size={11}
                        aria-hidden
                        className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[var(--text-ter)] dark:text-[var(--text-ter)]"
                      />
                    </span>
                  </label>
                  <button
                    onClick={() => removeParticipant(p.id)}
                    className="shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-luminous-coral-700 dark:hover:text-luminous-coral-400 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Remove"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* Search + available workspace members */}
        <div className="space-y-2">
          <div className="relative">
            <Users size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-ter)] dark:text-[var(--text-ter)] pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search workspace members…"
              className="w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] pl-8 pr-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-500 focus:outline-none focus:ring-1 focus:ring-slate-blue-500/40"
            />
          </div>
          {availableUsers.length === 0 ? (
            <p className="py-3 text-center text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {wsUsers.length === 0 ? 'Loading workspace members…' : search ? 'No matches.' : 'All workspace members have been added.'}
            </p>
          ) : (
            <div className="max-h-52 overflow-y-auto border border-[var(--rule)] dark:border-[var(--rule-bright)] divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
              {availableUsers.map((u) => {
                const label = u.name || u.email;
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => add(u.id, label)}
                    className="flex w-full items-center gap-2.5 px-3 py-2.5 text-left hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] transition-colors"
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface-raised)] text-xs font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                      {getInitials(label)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{u.name || u.email}</p>
                      {u.name && <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)] truncate">{u.email}</p>}
                    </div>
                    <Plus size={15} className="shrink-0 text-slate-blue-700 dark:text-slate-blue-400" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── S2 ──────────────────────────────────────────────────────────────────────

/** Whether the signed-in person runs this session: its facilitator or decider, or not a listed participant (its creator). */
function runsThisSession(
  event: { participants: Array<{ userId?: string; role: ParticipantRole }> },
  userId: string | null | undefined,
): boolean {
  const me = event.participants.find((participant) => participant.userId === userId);
  return !me || me.role === 'facilitator' || me.role === 'decider';
}

function ItemsEditor({ platformItems, collections }: { platformItems: PlatformItem[]; collections: CollectionWithItems[] }) {
  const event = useSessionEngineStore((s) => s.event)!;
  const addItems = useSessionEngineStore((s) => s.addItems);
  const removeItem = useSessionEngineStore((s) => s.removeItem);
  const setPoolState = useSessionEngineStore((s) => s.setPoolState);
  const [showModal, setShowModal] = useState(false);
  const [showSavePool, setShowSavePool] = useState(false);
  const comparabilityBlocks = useComparabilityBlocks(event);

  const itemIds = useMemo(() => event.items.map((i) => i.id), [event.items]);
  const exclusivityScopeIds = useMemo(
    () => Array.from(new Set([
      ...itemIds,
      ...platformItems.map((item) => item.id),
      ...collections.flatMap((collection) => collection.itemIds),
    ])),
    [itemIds, platformItems, collections],
  );
  const exclusivity = useDerivedExclusivity(exclusivityScopeIds);
  const currentUserId = useUserStore((s) => s.userId);
  const isFacilitator = runsThisSession(event, currentUserId);

  const isFrameLinked = event.contextMode === 'cycle_linked' && !!event.trackId;
  const platformItemCount = useMemo(() => event.items.filter((i) => i.source === 'platform').length, [event.items]);
  const authoredItemCount = Math.max(0, event.items.length - platformItemCount);

  const count = event.items.length;
  const poolableCount = useMemo(() => planItemPoolSave(event.items).poolable.length, [event.items]);
  const tier =
    count === 0 ? null
    : count <= 10 ? { label: 'Ideal (≤10)', cls: 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300' }
    : count <= 25 ? { label: 'Good (≤25)', cls: 'bg-slate-blue-100 text-slate-blue-800 dark:bg-slate-blue-900/40 dark:text-slate-blue-300' }
    : count < 50 ? { label: 'Acceptable (<50)', cls: 'bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300' }
    : { label: 'Too many (≥50) — narrow further', cls: 'bg-luminous-coral-100 text-luminous-coral-800 dark:bg-luminous-coral-900/30 dark:text-luminous-coral-300' };

  // Platform-sourced items are real records with an item detail page; typed /
  // imported / isolated items have prototype ids and no detail page.
  const isPlatformItem = (id: string, source: string) => source === 'platform' && !id.startsWith('seed-');

  return (
    <div className="space-y-4">
      <ComparabilityBanner blocks={comparabilityBlocks} />
      {/* Track item pool banner */}
      {isFrameLinked && (
        <div className={` px-3 py-2.5 text-xs ${platformItemCount > 0 ? 'bg-alpine-green-50 border border-alpine-green-200 text-[var(--text-sec)] dark:bg-alpine-green-900/20 dark:border-alpine-green-800/50 dark:text-[var(--text-sec)]' : 'bg-[var(--hover-tint)] border border-[var(--rule)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule-bright)] dark:text-[var(--text-sec)]'}`}>
          {platformItemCount > 0
            ? <><span className="text-alpine-green-800 dark:text-alpine-green-300 font-medium">{platformItemCount} item{platformItemCount !== 1 ? 's' : ''} inherited</span> from <strong>{event.trackName ?? 'this track'}</strong>'s item pool. {authoredItemCount > 0 ? `${authoredItemCount} authored item${authoredItemCount === 1 ? '' : 's'} added here.` : 'Add more or remove as needed.'}</>
            : <>No item pool in <strong>{event.trackName ?? 'this track'}</strong> yet. Add items below or connect a pool to the track first.</>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => setShowModal(true)}
          disabled={event.poolState === 'locked'}
          className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-2 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:cursor-not-allowed disabled:opacity-40"
          title={event.poolState === 'locked' ? 'Reopen the pool to add items' : 'Add items'}
        >
          <Plus size={16} /> Add items
        </button>
        <button
          type="button"
          onClick={() => setPoolState(event.poolState === 'locked' ? 'open' : 'locked')}
          className={[
            'flex items-center gap-1.5  border px-3 py-2 text-sm font-medium transition-colors',
            event.poolState === 'locked'
              ? 'border-electric-mustard-200 bg-electric-mustard-50 text-electric-mustard-800 hover:bg-electric-mustard-100 dark:border-electric-mustard-700/50 dark:bg-electric-mustard-900/20 dark:text-electric-mustard-300 dark:hover:bg-electric-mustard-900/30'
              : 'border-[var(--rule)] dark:border-[var(--rule-bright)] text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]',
          ].join(' ')}
        >
          {event.poolState === 'locked' ? <Unlock size={15} /> : <Lock size={15} />}
          {event.poolState === 'locked' ? 'Reopen pool' : 'Lock pool'}
        </button>
        {/*
          The S2 set becomes a record the platform can address — Input Scope in
          the Planning Engine, another session, a track. Before this the set
          existed only inside this session's config.
        */}
        <button
          type="button"
          onClick={() => setShowSavePool(true)}
          disabled={poolableCount === 0}
          title={
            poolableCount === 0
              ? 'A pool addresses platform items by id; none of these items are platform records yet'
              : `Save ${poolableCount} item${poolableCount === 1 ? '' : 's'} as a reusable item pool`
          }
          className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Save size={15} /> Save as item pool
        </button>
        <span className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">{count} item{count === 1 ? '' : 's'}</span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${event.poolState === 'locked' ? 'bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'}`}>
          Pool {event.poolState}
        </span>
        {tier && <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${tier.cls}`}>{tier.label}</span>}
      </div>

      {count === 0 ? (
        <EmptyHint>No items yet. The S2 output is a Collection — the manageable set carried into ranking.</EmptyHint>
      ) : (
        <ul className="divide-y divide-[var(--rule)] dark:divide-[var(--rule)]">
          {event.items.map((it) => (
            <li key={it.id} className="flex items-center justify-between py-2 text-sm">
              <span className="flex items-center gap-2 text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {it.title}
                <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${isFrameLinked && it.source === 'platform' ? 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300' : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-ter)]'}`}>
                  {isFrameLinked && it.source === 'platform' ? 'Track' : 'Authored'}
                </span>
                {isPlatformItem(it.id, it.source) && (
                  <Link
                    to={itemInContextHref(it.id, { cycleId: event.cycleId })}
                    title="Open item detail (relationships, evidence, history)"
                    className="text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-slate-blue-700 dark:hover:text-slate-blue-400"
                  >
                    <ExternalLink size={13} />
                  </Link>
                )}
              </span>
              <span className="flex items-center gap-3">
                <span className="text-[10px] uppercase text-[var(--text-ter)] dark:text-[var(--text-ter)]">{it.source}</span>
                <button onClick={() => removeItem(it.id)} className="text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-luminous-coral-700 dark:hover:text-luminous-coral-400" aria-label="Remove">
                  <Trash2 size={14} />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {count >= 2 && (
        <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          Relationships (competing, cannot-coexist, dependencies) live on each item's detail page. They surface read-only in scenario planning at S5.
        </p>
      )}

      {count > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] p-4">
          <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            <SlidersHorizontal size={15} className="text-[var(--text-ter)] dark:text-[var(--text-ter)]" /> Draft cut line
          </h3>
          <ScenarioBoard rankedItemIds={event.items.map((item) => item.id)} variant="slim" />
        </div>
      )}

      {showModal && (
        <ItemInputModal
          contextMode={event.contextMode}
          poolState={event.poolState}
          platformItems={platformItems}
          collections={collections}
          existingItemIds={event.items.map((i) => i.id)}
          existingTitles={event.items.map((i) => i.title)}
          exclusivity={exclusivity}
          stepMode={event.steps.S2.mode}
          isFacilitator={isFacilitator}
          onClose={() => setShowModal(false)}
          onAdd={addItems}
          onRemoveItem={removeItem}
        />
      )}

      {showSavePool && <SaveItemPoolModal onClose={() => setShowSavePool(false)} />}
    </div>
  );
}

// ── S3 ──────────────────────────────────────────────────────────────────────

/**
 * S3. While the track's criteria are locked, the server holds this session to
 * the track's criteria and weights (1031, 1048), so they are shown read-only
 * with the reason and no control is drawn (build contract §2.8). While the
 * track is open and sets weights, the editor says the starting weights came
 * from it.
 */
function CriteriaEditor({
  trackLocked = false,
  trackWeights = null,
}: {
  trackLocked?: boolean;
  trackWeights?: Record<string, number> | null;
}) {
  const event = useSessionEngineStore((s) => s.event)!;
  if (trackLocked) return <LockedCriteria criteria={event.criteria} weighted={trackWeights !== null} />;
  return <CriteriaWeightsEditor trackWeights={trackWeights} />;
}

/** The track's criteria and weights, as this session scores with them. Nothing to change here. */
function LockedCriteria({
  criteria,
  weighted,
}: {
  criteria: readonly { id: string; name: string; weight: number }[];
  weighted: boolean;
}) {
  const total = criteria.reduce((s, c) => s + c.weight, 0) || 1;
  return (
    <div className="space-y-3">
      <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        {weighted
          ? 'Criteria and weights are set and locked on the track. This session scores with them.'
          : 'Criteria are set and locked on the track. The track sets no weights, so they share evenly.'}
      </p>
      {criteria.length === 0 ? (
        <EmptyHint>The track's criteria have not reached this session yet.</EmptyHint>
      ) : (
        <ul className="space-y-1.5" aria-label="Criteria and weights">
          {criteria.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-[var(--text-sec)] dark:text-[var(--text-sec)]">{c.name}</span>
              <span className="tabular-nums font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                {Math.round((c.weight / total) * 100)}%
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CriteriaWeightsEditor({ trackWeights }: { trackWeights: Record<string, number> | null }) {
  const event = useSessionEngineStore((s) => s.event)!;
  const setCriteria = useSessionEngineStore((s) => s.setCriteria);
  const [name, setName] = useState('');
  const [lockedIds, setLockedIds] = useState<Set<string>>(new Set());

  const systemAttributes = useAttributesStore((s) => s.attributes);
  const criteria = event.criteria;
  const isFrameLinked = event.contextMode === 'cycle_linked' && !!event.trackId;
  const authoredCriteriaCount = criteria.filter((c) => c.id.startsWith('crit_')).length;
  const inheritedCriteriaCount = Math.max(0, criteria.length - authoredCriteriaCount);

  // Work in integer percentages (sum ≈ 100).
  // Weights are stored as percentages; normalize on display.
  const totalWeight = criteria.reduce((s, c) => s + c.weight, 0) || 1;
  const toPct = (w: number) => Math.round((w / totalWeight) * 100);

  // Locked percentage total — caps what the unlocked ones can claim
  const lockedTotal = criteria
    .filter((c) => lockedIds.has(c.id))
    .reduce((s, c) => s + toPct(c.weight), 0);

  // System attributes not yet added as criteria
  const importableAttrs = useMemo(
    () => systemAttributes.filter((a) => !criteria.some((c) => c.id === a.key || c.name === a.label)),
    [systemAttributes, criteria],
  );

  /**
   * Equal-share every criterion, summing to exactly 100. `Math.floor(100 / n)`
   * stored three criteria as 33/33/33 — a set that does not sum to 100, which
   * every downstream reader then renormalises differently, so the setup screen,
   * the participant screen and the score itself each showed a different
   * percentage for the same criterion.
   */
  function equalShare<T extends { id: string; name: string; weight: number }>(list: T[]): T[] {
    return normalizeCriteriaWeights(
      list.map((c) => ({ ...c, weight: 1 })),
    ) as unknown as T[];
  }

  function add() {
    const n = name.trim();
    if (!n) return;
    // Equal-share the new criterion among all unlocked (normalise to 100)
    setCriteria(
      equalShare([
        ...criteria,
        { id: `crit_${Math.random().toString(36).slice(2, 8)}`, name: n, weight: 0 },
      ]),
    );
    setName('');
  }

  function importAttr(key: string, label: string) {
    if (criteria.some((c) => c.id === key)) return;
    setCriteria(equalShare([...criteria, { id: key, name: label, weight: 0 }]));
  }

  function resetEqual() {
    setLockedIds(new Set());
    setCriteria(equalShare(criteria));
  }

  function toggleLock(id: string) {
    setLockedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function handleWeightChange(id: string, newPct: number) {
    const available = 100 - lockedTotal;
    const capped = Math.max(1, Math.min(newPct, available - (criteria.filter((c) => c.id !== id && !lockedIds.has(c.id)).length)));
    const otherUnlocked = criteria.filter((c) => c.id !== id && !lockedIds.has(c.id));
    const currentOtherTotal = otherUnlocked.reduce((s, c) => s + toPct(c.weight), 0);
    const remaining = available - capped;

    setCriteria(
      criteria.map((c) => {
        if (c.id === id) return { ...c, weight: capped };
        if (lockedIds.has(c.id)) return c; // untouched
        if (otherUnlocked.length === 0) return c;
        const newW =
          currentOtherTotal === 0
            ? Math.max(1, Math.round(remaining / otherUnlocked.length))
            : Math.max(1, Math.round((toPct(c.weight) / currentOtherTotal) * remaining));
        return { ...c, weight: newW };
      }),
    );
  }

  return (
    <div className="space-y-4">
      {/* The structural moment: is this actually a set of legitimate alternatives?
          Asked before criteria are finalized, in canon Rule 13 order. Gated on
          the comparison_set_validation module + flag. */}
      <ComparisonSetValidationPanel />
      {/* Strategic driver coverage + driver-weight proposals (cycle-linked only) */}
      <DriverAlignmentPanel />
      {trackWeights && criteria.some((c) => c.id in trackWeights) && (
        <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          Starting weights come from the track ({describeTrackWeights(
            trackWeights,
            (key) => criteria.find((c) => c.id === key)?.name ?? humanizeAttributeKey(key),
          )}). You can change them for this session while the track's criteria are open.
        </p>
      )}
      {criteria.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] px-3 py-2 text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <span className="font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">{criteria.length} criteria</span>
          {isFrameLinked && inheritedCriteriaCount > 0 && (
            <span className="ml-2 text-alpine-green-700 dark:text-alpine-green-300">{inheritedCriteriaCount} inherited/system</span>
          )}
          {authoredCriteriaCount > 0 && (
            <span className="ml-2 text-[var(--text-ter)] dark:text-[var(--text-ter)]">{authoredCriteriaCount} authored</span>
          )}
        </div>
      )}
      {/* System attributes import */}
      {importableAttrs.length > 0 && (
        <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--surface)] dark:bg-[var(--surface)] p-3">
          <div className="mb-2 flex items-center gap-1.5">
            <Library size={13} className="text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
            <p className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]">Pull from system attributes</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {importableAttrs.map((a) => (
              <button
                key={a.key}
                onClick={() => importAttr(a.key, a.label)}
                className="flex items-center gap-1 rounded-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-2.5 py-1 text-[11px] font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:border-slate-blue-400 hover:bg-slate-blue-50 dark:hover:bg-slate-blue-900/30 hover:text-slate-blue-700 dark:hover:text-slate-blue-200 transition-colors"
              >
                <Plus size={10} /> {a.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && add()}
          placeholder="Or type a custom criterion…"
          className="flex-1 border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:border-slate-blue-400 focus:outline-none focus:ring-1 focus:ring-slate-blue-100 dark:focus:ring-slate-blue-700"
        />
        <button onClick={add} className="border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-2 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]">
          <Plus size={16} />
        </button>
      </div>

      {criteria.length === 0 ? (
        <EmptyHint>No criteria yet. Pull from system attributes above, or type a custom one.</EmptyHint>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <p className="text-[11px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              {lockedIds.size > 0
                ? `${lockedIds.size} locked · ${100 - lockedTotal}% available to redistribute`
                : 'Click the lock to freeze a weight while adjusting others'}
            </p>
            <button onClick={resetEqual} className="text-xs font-medium text-slate-blue-700 dark:text-slate-blue-400 hover:underline">
              Reset equal
            </button>
          </div>
          <ul className="space-y-3">
            {criteria.map((c) => {
              const pct = toPct(c.weight);
              const isLocked = lockedIds.has(c.id);
              const available = 100 - lockedTotal;
              return (
                <li key={c.id} className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    {/* Lock toggle */}
                    <button
                      onClick={() => toggleLock(c.id)}
                      title={isLocked ? 'Unlock this weight' : 'Lock this weight'}
                      className={[
                        'shrink-0 rounded p-0.5 transition',
                        isLocked
                          ? 'text-electric-mustard-600 hover:text-electric-mustard-400'
                          : 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]',
                      ].join(' ')}
                    >
                      {isLocked ? <Hash size={13} /> : <Hash size={13} className="opacity-40" />}
                    </button>

                    <span className={`flex-1 ${isLocked ? 'font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]' : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'}`}>
                      {c.name}
                    </span>
                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide ${isFrameLinked && !c.id.startsWith('crit_') ? 'bg-alpine-green-100 text-alpine-green-800 dark:bg-alpine-green-900/30 dark:text-alpine-green-300' : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-ter)]'}`}>
                      {isFrameLinked && !c.id.startsWith('crit_') ? 'Track' : 'Authored'}
                    </span>

                    {/* Percentage readout */}
                    <span className={`w-10 text-right tabular-nums text-sm font-semibold ${isLocked ? 'text-electric-mustard-700 dark:text-electric-mustard-400' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
                      {pct}%
                    </span>

                    {/* Remove */}
                    <button
                      onClick={() => {
                        setLockedIds((prev) => { const n = new Set(prev); n.delete(c.id); return n; });
                        setCriteria(criteria.filter((x) => x.id !== c.id));
                      }}
                      className="text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-luminous-coral-700 dark:hover:text-luminous-coral-400 shrink-0"
                      aria-label="Remove"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Slider — disabled + tinted when locked */}
                  <div className="flex items-center gap-2 pl-6">
                    <input
                      type="range"
                      min={1}
                      max={isLocked ? pct : Math.max(1, available - (criteria.filter((x) => x.id !== c.id && !lockedIds.has(x.id)).length))}
                      step={1}
                      value={pct}
                      disabled={isLocked}
                      onChange={(e) => handleWeightChange(c.id, Number(e.target.value))}
                      className={[
                        'flex-1 h-1.5 rounded appearance-none transition',
                        isLocked ? 'accent-electric-mustard-500 cursor-not-allowed opacity-60' : 'accent-slate-blue-600 cursor-pointer',
                      ].join(' ')}
                    />
                    {isLocked && (
                      <span className="text-[10px] uppercase tracking-wide text-electric-mustard-600 font-semibold">locked</span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          {/* Visual weight bar */}
          {criteria.length > 1 && (
            <div className="flex h-2 w-full overflow-hidden rounded-full">
              {criteria.map((c, i) => {
                const pct = toPct(c.weight);
                const colors = ['bg-slate-blue-500 dark:bg-slate-blue-900/30', 'bg-alpine-green-500', 'bg-electric-mustard-500 dark:bg-electric-mustard-900/30', 'bg-luminous-coral-500 dark:bg-luminous-coral-900/30', 'bg-sienna-500'];
                return (
                  <div
                    key={c.id}
                    style={{ width: `${pct}%` }}
                    className={`${colors[i % colors.length]} ${lockedIds.has(c.id) ? 'opacity-100' : 'opacity-70'} transition-all`}
                    title={`${c.name}: ${pct}%`}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── S4 ──────────────────────────────────────────────────────────────────────

/**
 * The item × criterion grid, where each column behaves by where the track said its
 * values come from (src/lib/valuesFrom.ts, migration 1062):
 *   the record      — the item's official value, read here, never typed; a blank is
 *                     named with a link to the item, where whoever may can set it
 *   one person      — typeable only for them; everyone else reads "waiting on …"
 *   the facilitator — typed here, for this session (the grid as it always was)
 *   the room        — scored in the session; the room's combined score is pulled in
 *                     by itself once the sitting is over (nobody retypes it)
 *   the method      — worked out by the method; read here
 * A criterion nobody has spoken for behaves as the facilitator's, and the panel says
 * nobody has said, with the link to say it.
 */
function ValuesEditor() {
  const event = useSessionEngineStore((s) => s.event)!;
  const setValue = useSessionEngineStore((s) => s.setValue);
  const hydrateValues = useSessionEngineStore((s) => s.hydrateValues);
  const session = useSessionStore((s) => s.sessions.find((x) => x.id === event.id));
  const currentUserId = useUserStore((s) => s.userId);
  const [scaleError, setScaleError] = useState<string | null>(null);
  const { users } = useSystemUsers();
  // Which connected system keeps a criterion's value (canon 01 › Field Source): the column
  // reads "the record · Jira", and a column the room or a person fills says that its
  // value stays this session's and never reaches that system.
  const s4WorkspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const keepers = useAttributeKeepers(s4WorkspaceId);

  // The value range is the session's own (tool_config scoring_scale_min/max,
  // #1347): the one the live scoring screens read. This grid used to be fixed
  // at 0–10 while the room scored 1–5. It is chosen here while the session is
  // a draft, by whoever runs it — unless its track has locked one (S-39, 1038):
  // then the server takes no other, and the only move is onto the track's.
  const scale = readToolSettings(session?.toolConfig).scoringScale;
  const { trackScale, scaleLocked } = useTrackStepLocks(event.trackId);
  const heldByTrack = !!trackScale && scaleLocked;
  const runsIt = !!session && session.state === 'DRAFT' && runsThisSession(event, currentUserId);
  const mayChooseScale = runsIt && !heldByTrack;
  const offTrackScale = heldByTrack && !isSameScoringScale(scale, trackScale);
  const chooseScale = async (next: ScoringScaleRange) => {
    if (!session) return;
    setScaleError(null);
    const result = await useSessionStore.getState().updateSession(session.id, {
      toolConfig: { ...normalizeToolConfig(session.toolConfig), ...scoringScaleToolConfig(next) },
    });
    if (!result.success) setScaleError(`The value range was not saved: ${result.error ?? 'unknown error'}`);
  };
  const outsideRange = event.items.reduce(
    (n, it) =>
      n + event.criteria.filter((c) => {
        const v = event.values[it.id]?.[c.id];
        return typeof v === 'number' && (v < scale.min || v > scale.max);
      }).length,
    0,
  );

  // Where each column's values come from, and what that makes each cell for the
  // person looking. Criteria made in this session (crit_*) carry no answer: they
  // are the facilitator's.
  const nameOfUser = (id: string | null | undefined) => users.find((u) => u.id === id)?.name ?? null;
  const columns = useMemo(
    () => event.criteria.map((c) => ({
      ...c,
      from: c.valuesFrom ?? null,
      personId: c.valuesFromPersonId ?? null,
      policy: s4CellPolicy(c.valuesFrom ?? null, c.valuesFromPersonId ?? null, currentUserId),
      keptBy: c.id.startsWith('crit_') ? null : keepers.keeperName(c.id),
    })),
    [event.criteria, currentUserId, keepers],
  );
  const keeperNotes = columns.map((c) => keeperNote(c.name, c.keptBy, c.from)).filter((n): n is string => !!n);
  const anySaid = columns.some((c) => c.from);
  const unsaid = columns.filter((c) => !c.from && !c.id.startsWith('crit_'));
  const roomColumns = columns.filter((c) => needsTheRoom(c.from));
  const recordColumns = columns.filter((c) => c.from === 'any_group_member');
  const sentence = anySaid
    ? valuesFromSentence(columns.map((c) => ({ name: c.name, from: c.from, personName: c.personId ? nameOfUser(c.personId) ?? 'one person' : null, keptBy: c.keptBy })))
    : '';

  // Pull in values the workspace already accepted for these items. For a
  // system-pulled criterion the criterion id is the attribute key, so
  // accepted_values maps onto this grid one-to-one. Runs once per item/criteria
  // shape; hydrateValues never overwrites a value entered in this event.
  const itemKey = event.items.map((i) => i.id).join(',');
  const critKey = event.criteria.map((c) => c.id).join(',');
  const [onRecord, setOnRecord] = useState<Record<string, Record<string, number>>>({});
  useEffect(() => {
    const itemIds = event.items.map((i) => i.id).filter((id) => !id.startsWith('item_'));
    const criterionIds = event.criteria.map((c) => c.id);
    if (itemIds.length === 0 || criterionIds.length === 0) return;
    let cancelled = false;
    void (async () => {
      const { fetchAcceptedValueGrid } = await import('../../../lib/acceptedValuePrefill');
      const merged = await fetchAcceptedValueGrid(itemIds, criterionIds, event.id, scale);
      if (cancelled) return;
      setOnRecord(merged);
      if (Object.keys(merged).length > 0) hydrateValues(merged);
    })();
    return () => { cancelled = true; };
    // Keyed on the joined id lists, not the arrays themselves: event.items and
    // event.criteria are rebuilt every render, so listing them here would refetch
    // on every keystroke in the grid. hydrateValues is a stable store action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemKey, critKey]);

  // The room's combined scores, for columns the room or the method fill: read from
  // criterion_scores_by_session (842) for this session and its S4 sitting, once the
  // sitting is over. Written into the grid as the column's value, so S5 computes
  // from what the room decided and nobody retypes it. Re-read when S4's run finishes.
  const roomKey = roomColumns.map((c) => c.id).join(',');
  const s4Run = event.steps.S4?.runStatus;
  const s4Child = event.childSessions?.S4 ?? null;
  const [roomScores, setRoomScores] = useState<Record<string, Record<string, { mean: number; n: number }>>>({});
  useEffect(() => {
    if (!roomKey || event.id.startsWith('evt_')) return;
    let cancelled = false;
    void (async () => {
      const { fetchRoomScores } = await import('../../../lib/sessionRoomScores');
      const scores = await fetchRoomScores([event.id, s4Child].filter((x): x is string => !!x), roomColumns.map((c) => c.id));
      if (cancelled) return;
      setRoomScores(scores);
      for (const [itemId, row] of Object.entries(scores)) {
        for (const [critId, cell] of Object.entries(row)) {
          if (event.values[itemId]?.[critId] !== cell.mean) setValue(itemId, critId, cell.mean);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomKey, itemKey, s4Run, s4Child, event.id]);

  if (event.items.length === 0 || event.criteria.length === 0) {
    return <EmptyHint>Add items (S2) and criteria (S3) first — values are set on the item × criterion grid, from {scale.min} to {scale.max}.</EmptyHint>;
  }

  const recordBlanks = recordColumns.map((c) => ({
    name: c.name,
    items: event.items.filter((it) => typeof onRecord[it.id]?.[c.id] !== 'number'),
  })).filter((b) => b.items.length > 0);
  const s4Declared = event.steps.S4?.mode === 'declared';
  const trackHref = event.trackId ? `/prioritization/tracks/${event.trackId}?tab=criteria` : null;

  return (
    <div className="overflow-x-auto">
      <div className="mb-3">
        {mayChooseScale ? (
          <>
            <ScoringScaleChoice value={scale} onChange={(next) => void chooseScale(next)} legend="Value range" />
            {trackScale && (
              <p className="mt-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]" data-testid="s4-track-range">
                The track sets {scoringScaleLabel(trackScale)}. This session may change it until the track locks its range.
              </p>
            )}
          </>
        ) : (
          <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]" data-testid="s4-value-range">
            Value range: {scoringScaleLabel(scale)}{heldByTrack && !offTrackScale ? ', set and locked on the track' : ''}. Everyone scores each item from {scale.min} (low) to {scale.max} (high) on every criterion.
          </p>
        )}
        {offTrackScale && trackScale && (
          <p className="mt-1 text-xs text-[var(--mustard)]" data-testid="s4-off-track-range">
            The track has locked its range at {scoringScaleLabel(trackScale)}; this session was set up on {scoringScaleLabel(scale)}.{' '}
            {runsIt ? (
              <button type="button" className="underline" onClick={() => void chooseScale(trackScale)}>
                Score on {scoringScaleLabel(trackScale)}
              </button>
            ) : (
              'Whoever runs the session can move it onto the track’s range.'
            )}
          </p>
        )}
        {scaleError && <p className="mt-1 text-xs text-[var(--coral)]">{scaleError}</p>}
        {outsideRange > 0 && (
          <p className="mt-1 text-xs text-[var(--mustard)]" data-testid="s4-outside-range">
            {outsideRange} value{outsideRange !== 1 ? 's are' : ' is'} outside {scoringScaleLabel(scale)}, marked below. Retype {outsideRange !== 1 ? 'them' : 'it'} on this range.
          </p>
        )}
      </div>

      {/* Where the values come from: one sentence, then what follows from it. */}
      {anySaid ? (
        <p className="mb-2 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]" data-testid="s4-values-from">
          {sentence}.
          {trackHref && <> <Link to={trackHref} className="text-xs text-[var(--text-ter)] underline">Change on the track</Link></>}
        </p>
      ) : (
        <p className="mb-2 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]" data-testid="s4-values-from-unsaid">
          Blank cells start from the workspace&apos;s official values, or values already saved for this session — never another session&apos;s. What you type here counts for this session only; it does not change an official value.
          {trackHref && <> Nobody has said where these values come from; <Link to={trackHref} className="underline">say it on the track</Link>.</>}
        </p>
      )}
      {anySaid && unsaid.length > 0 && (
        <p className="mb-2 text-xs text-[var(--mustard)]" data-testid="s4-values-from-partly">
          Nobody has said where {unsaid.map((c) => c.name).join(', ')} {unsaid.length === 1 ? 'comes' : 'come'} from; until then the facilitator may type {unsaid.length === 1 ? 'it' : 'them'} here.
        </p>
      )}
      {keeperNotes.length > 0 && (
        <p className="mb-2 text-xs text-[var(--mustard)]" data-testid="s4-keeper-note">
          {keeperNotes.join(' ')}
        </p>
      )}
      {roomColumns.length > 0 && s4Declared && (
        <p className="mb-2 text-xs text-[var(--mustard)]" data-testid="s4-needs-room">
          {roomColumns.map((c) => c.name).join(' and ')} {roomColumns.length === 1 ? 'is' : 'are'} {roomColumns.every((c) => c.from === 'tool_result') ? 'worked out by the method' : 'scored by the room'}: run S4 collaboratively above, or change where {roomColumns.length === 1 ? 'it comes' : 'they come'} from on the track.
        </p>
      )}
      {recordBlanks.length > 0 && (
        <p className="mb-2 text-xs text-[var(--mustard)]" data-testid="s4-record-blanks">
          {recordBlanks.map((b) => `${b.items.length} item${b.items.length === 1 ? ' has' : 's have'} no ${b.name} on record`).join(' · ')}. Set {recordBlanks.length === 1 && recordBlanks[0].items.length === 1 ? 'it' : 'them'} on the item{' '}
          ({recordBlanks.flatMap((b) => b.items).filter((it, i, all) => all.findIndex((x) => x.id === it.id) === i).slice(0, 4).map((it, i) => (
            <span key={it.id}>{i > 0 ? ', ' : ''}<Link to={itemInContextHref(it.id, { cycleId: event.cycleId ?? null })} className="underline">{it.title}</Link></span>
          ))}
          {recordBlanks.flatMap((b) => b.items).length > 4 ? ', …' : ''}).
        </p>
      )}

      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="sticky left-0 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-2 py-2 text-left font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">Item</th>
            {columns.map((c) => (
              <th key={c.id} className="px-2 py-2 text-center font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                {c.name}
                {c.from && (
                  <span className="block text-[10px] font-normal text-[var(--text-ter)]" data-testid={`s4-col-from-${c.id}`}>
                    {c.policy === 'record' ? (c.keptBy ? `the record · ${c.keptBy}` : 'the record')
                      : c.policy === 'mine' ? 'yours to set'
                      : c.policy === 'waiting' ? `waiting on ${nameOfUser(c.personId) ?? 'one person'}`
                      : c.policy === 'room' ? 'the room'
                      : c.policy === 'method' ? 'the method'
                      : 'set here'}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {event.items.map((it) => (
            <tr key={it.id} className="border-t border-[var(--rule)] dark:border-[var(--rule)]">
              <td className="sticky left-0 bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-2 py-1.5 text-[var(--text-pri)] dark:text-[var(--text-pri)]">{it.title}</td>
              {columns.map((c) => {
                const v = event.values[it.id]?.[c.id];
                const outside = typeof v === 'number' && (v < scale.min || v > scale.max);
                const typeable = c.policy === 'typed' || c.policy === 'mine';
                if (!typeable) {
                  const room = roomScores[it.id]?.[c.id];
                  const text = typeof v === 'number' ? String(Math.round(v * 10) / 10) : '—';
                  const title = c.policy === 'record'
                    ? (typeof onRecord[it.id]?.[c.id] === 'number' ? 'The item’s official value' : 'No value on record')
                    : c.policy === 'waiting' ? `Waiting on ${nameOfUser(c.personId) ?? 'one person'}`
                    : room ? `${room.n} scored; the room’s combined score` : c.policy === 'room' ? 'Scored by the room when S4 runs' : 'Worked out by the method';
                  return (
                    <td key={c.id} className="px-2 py-1.5 text-center" title={title} data-testid={`s4-cell-${c.policy}`}>
                      <span className={['inline-block w-14 rounded px-1.5 py-1 tabular-nums', typeof v === 'number' ? 'text-[var(--text-pri)] bg-[var(--surface)]' : 'text-[var(--text-ter)]'].join(' ')}>
                        {text}
                      </span>
                      {room && room.n > 1 && <span className="ml-1 text-[10px] text-[var(--text-ter)]">×{room.n}</span>}
                    </td>
                  );
                }
                return (
                  <td key={c.id} className="px-2 py-1.5 text-center">
                    <input
                      type="number"
                      min={scale.min}
                      max={scale.max}
                      value={v ?? ''}
                      aria-label={`${it.title}, ${c.name}`}
                      aria-invalid={outside || undefined}
                      title={outside ? `Outside ${scoringScaleLabel(scale)}` : undefined}
                      onChange={(e) => setValue(it.id, c.id, Math.max(scale.min, Math.min(scale.max, Number(e.target.value))))}
                      className={[
                        'w-14 rounded border bg-[var(--bg-raised)] dark:bg-[var(--surface)] text-[var(--text-pri)] dark:text-[var(--text-pri)] px-1.5 py-1 text-center focus:border-slate-blue-400 focus:outline-none',
                        outside ? 'border-[var(--mustard)]' : 'border-[var(--rule)] dark:border-[var(--rule-bright)]',
                      ].join(' ')}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── S5 ──────────────────────────────────────────────────────────────────────

function RankingEditor() {
  const event = useSessionEngineStore((s) => s.event)!;
  const runRanking = useSessionEngineStore((s) => s.runRanking);
  const setManualRanking = useSessionEngineStore((s) => s.setManualRanking);
  const { exchangeState, activeLoop } = useExchangeContext(event.contextMode === 'cycle_linked' ? event.cycleId : undefined);
  const [showLift, setShowLift] = useState(false);
  // The guard has to bite here too, not only at session creation — this is the
  // step that produces the ordering the rule is about.
  const comparabilityBlocks = useComparabilityBlocks(event);

  const unmet = unmetRequirements(event);
  const ready = canRunRanking(event);
  const titleById = useMemo(() => {
    const m = new Map<string, string>();
    event.items.forEach((i) => m.set(i.id, i.title));
    return m;
  }, [event.items]);
  // Which Loop this proposal belongs to. NOT activeLoop: that is the cycle's
  // active loop, and lifting from a Test Lab session attached the proposal to
  // the Design Engineering loop — a wrong answer is worse than the null this
  // replaced. resolveLoopIdForSession asks in the right order: the loop named
  // in the URL, then the loop that actually holds this session, then the track's.
  const [liftSearchParams] = useSearchParams();
  const catchballLoops = useCatchballLoopStore((s) => s.loops);
  const liftLoopId = resolveLoopIdForSession({
    urlLoopId: liftSearchParams.get('loopId'),
    sessionId: event.id,
    trackId: event.trackId ?? null,
    cycleId: event.cycleId ?? null,
    loops: catchballLoops,
  });

  const catchballHref = activeLoop
    ? buildCatchballInboxUrl(activeLoop.cycleId, activeLoop.id)
    : event.cycleId
      ? `/prioritization/cascade/${event.cycleId}`
      : '/prioritization/cascade';

  function ensureManualSeed() {
    if (event.ranking.length === 0) setManualRanking(event.items.map((i) => i.id));
  }
  function move(idx: number, dir: -1 | 1) {
    const next = [...event.ranking];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setManualRanking(next);
  }

  return (
    <div className="space-y-4">
      {unmet.length > 0 && (
        <div className="flex items-start gap-2 bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/30 dark:text-electric-mustard-300 px-3 py-2 text-sm">
          <AlertCircle size={15} className="mt-0.5 shrink-0" />
          <span>
            Prelims pending before ranking: {unmet.map((u) => u.label).join(' · ')}
          </span>
        </div>
      )}

      <ComparabilityBanner blocks={comparabilityBlocks} />

      <div className="flex flex-wrap gap-2">
        <button
          onClick={runRanking}
          disabled={!ready || comparabilityBlocks.length > 0}
          className="flex items-center gap-1.5 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-2 text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
          title={
            comparabilityBlocks.length > 0
              ? `Not comparable: ${comparabilityBlocks.map((b) => b.name).join('; ')}`
              : ready ? 'Rank by weighted value × criteria weight' : 'Satisfy prelims first'
          }
        >
          <Play size={15} /> Compute from values
        </button>
        <button
          onClick={ensureManualSeed}
          disabled={event.items.length === 0}
          className="border border-[var(--rule)] dark:border-[var(--rule-bright)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-40"
        >
          Order manually
        </button>
      </div>

      {event.ranking.length === 0 ? (
        <EmptyHint>No ranking yet. Compute it from values, or order manually. This is the S5 output — the main event.</EmptyHint>
      ) : (
        <ol className="overflow-hidden border border-[var(--rule)] dark:border-[var(--rule-bright)]">
          {event.ranking.map((id, idx) => (
            <li key={id} className="flex items-center gap-3 border-t border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm first:border-t-0">
              <span className="w-6 text-right tabular-nums text-[var(--text-ter)] dark:text-[var(--text-ter)]">{idx + 1}</span>
              <span className="flex-1 text-[var(--text-pri)] dark:text-[var(--text-pri)]">{titleById.get(id) ?? id}</span>
              <ItemRelationshipFlags itemId={id} />
              <span className="tabular-nums text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">{scoreItem(event, id).toFixed(1)}</span>
              <span className="flex items-center gap-0.5">
                <button onClick={() => move(idx, -1)} className="rounded p-1 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]" aria-label="Up"><ArrowUp size={13} /></button>
                <button onClick={() => move(idx, 1)} className="rounded p-1 text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]" aria-label="Down"><ArrowDown size={13} /></button>
              </span>
            </li>
          ))}
        </ol>
      )}

      <div className="border border-[var(--rule)] dark:border-[var(--rule-bright)] p-4">
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          <SlidersHorizontal size={15} className="text-[var(--text-ter)] dark:text-[var(--text-ter)]" /> Scenario planning — cut line
        </h3>
        <ScenarioBoard />
      </div>

      {event.contextMode === 'cycle_linked' && (
        <div className="border border-slate-blue-200 dark:border-slate-blue-700/50 bg-slate-blue-50 dark:bg-slate-blue-900/20 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-slate-blue-800 dark:text-slate-blue-200">Return proposal through Exchange</p>
              <p className="text-xs leading-relaxed text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                Lift this ranking into the governance lane — Catchball → review → approval → ratification.
                {event.ranking.length === 0 && ' Produce a ranking first.'}
              </p>
              {exchangeState !== 'not_applicable' && (
                <p className="text-[11px] font-medium uppercase tracking-wide text-electric-mustard-700 dark:text-electric-mustard-400">
                  {exchangeState === 'revision_received' ? 'Revision context active' : 'Request context active'}
                </p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {/* The ranking had nowhere to go. A priority list enters the
                  governance lane only through Lift to Proposal, and that action
                  lived solely on the legacy SessionDetail page — so a ranking
                  produced here could never be proposed, and Ratification found
                  nothing and reported "no priority lists were in proposed status"
                  while still succeeding. Same modal, same RPC, reachable from the
                  surface that actually produces the ranking. */}
              <button
                type="button"
                onClick={() => setShowLift(true)}
                disabled={event.ranking.length === 0 || !event.cycleId}
                className="inline-flex items-center gap-1.5 bg-slate-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-slate-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ScrollText size={12} />
                Lift to Proposal
              </button>
              <Link
                to={catchballHref}
                className="inline-flex shrink-0 items-center gap-1.5 border border-slate-blue-300 dark:border-slate-blue-700 px-3 py-2 text-xs font-medium text-slate-blue-700 dark:text-slate-blue-300 hover:bg-slate-blue-100 dark:hover:bg-slate-blue-900/40"
              >
                Open Catchball
                <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        </div>
      )}

      {showLift && event.cycleId && (
        <LiftToProposalModal
          isOpen={showLift}
          onClose={() => setShowLift(false)}
          cycleId={event.cycleId}
          cycleName={event.cycleName ?? undefined}
          // Without this the lift succeeds and the proposal is orphaned: the
          // modal forwards loopId straight to lift_priority_proposal, and the
          // Designer was the one caller that never passed it. The result was a
          // proposal holding the right ten items with loop_id null, the Loop's
          // proposal_id null, and the Loop still sitting at middle_working —
          // so the ball had nothing to carry back up the path it came down.
          // activeLoop is already resolved here; the Open Catchball link beside
          // this button uses it.
          loopId={liftLoopId ?? undefined}
          defaultSessionId={event.id}
          onLifted={() => { /* modal shows its own success state */ }}
        />
      )}
    </div>
  );
}

// ── shared ────────────────────────────────────────────────────────────────

function ComparabilityBanner({ blocks }: { blocks: Array<{ name: string; detail: string }> }) {
  if (blocks.length === 0) return null;
  return (
    <div className="border border-luminous-coral-300 dark:border-luminous-coral-700 bg-luminous-coral-50 dark:bg-luminous-coral-900/20 p-4 space-y-2">
      <p className="text-sm font-semibold text-luminous-coral-900 dark:text-luminous-coral-200">
        This pool isn&rsquo;t comparable yet
      </p>
      {blocks.map((b) => (
        <div key={b.name} className="text-xs leading-relaxed text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          <span className="font-medium">{b.name}</span> — {b.detail}
        </div>
      ))}
      <p className="text-xs text-[var(--text-sec)] dark:text-[var(--text-ter)]">
        Ranking stays available for sets that can be legitimately compared. Narrow the
        pool to one category &times; altitude cell, or resolve the flagged items.
      </p>
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="bg-[var(--surface)] dark:bg-[var(--surface)] px-4 py-4 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">{children}</p>;
}
